/**
 * `HoverTooltip` — small floating card used by the bar charts in place of the
 * native browser `title` tooltip. Positioned with viewport (fixed) coords so
 * it isn't clipped by any ancestor `overflow-hidden` (the suite cards have
 * one). Content is provided as children; this component owns only the chrome
 * (border, shadow, arrow, positioning).
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';

export interface HoverTooltipProps {
  /** Viewport x of the anchor cell's horizontal center. */
  x: number;
  /** Viewport y of the anchor cell's top edge. */
  y: number;
  children: ReactNode;
}

export function HoverTooltip({ x, y, children }: HoverTooltipProps) {
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full"
      style={{ left: x, top: y - 10 }}
    >
      <div className="relative rounded-md border border-line bg-paper-1 px-3 py-2 text-[12px] leading-[1.45] text-ink-1 shadow-pop">
        {children}
        <span
          aria-hidden
          className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-line bg-paper-1"
        />
      </div>
    </div>
  );
}

// `major` is the orange band the 90d day ladder can produce (see
// `lib/bucket-severity.ts`). `LagTone` stays a strict subset of this union, so
// the lag tooltips keep type-checking unchanged.
export type TipTone = 'ok' | 'warn' | 'major' | 'down' | 'none';

const TONE_DOT: Record<TipTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  major: 'bg-major',
  down: 'bg-down',
  none: 'bg-line-strong',
};

// Deliberately NOT the suite ramp's words. `suite-severity.ts` already spends
// "Major outage" on its ORANGE band and "Service down" on its red one; reusing
// either here would make one word name two colours of different rank on the
// same page. `down` keeps its original wording so the lag tooltips, which feed
// `LagTone` into this same header, read exactly as they did before.
const TONE_LABEL: Record<TipTone, string> = {
  ok: 'Operational',
  warn: 'Degraded',
  major: 'Heavily degraded',
  down: 'Outage',
  none: 'No data',
};

const TONE_TEXT: Record<TipTone, string> = {
  ok: 'text-ok-fg',
  warn: 'text-warn-fg',
  major: 'text-major-fg',
  down: 'text-down-fg',
  none: 'text-ink-2',
};

export function TipHeader({ tone }: { tone: TipTone }) {
  return (
    <div className="flex items-center gap-2 pb-1">
      <span
        className={clsx('inline-block h-2 w-2 rounded-full', TONE_DOT[tone])}
      />
      <span className={clsx('text-[12.5px] font-semibold', TONE_TEXT[tone])}>
        {TONE_LABEL[tone]}
      </span>
    </div>
  );
}

export function TipTime({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[11.5px] tracking-[-.01em] text-ink-2">
      {children}
    </div>
  );
}

export function TipDetail({ children }: { children: ReactNode }) {
  return (
    <div className="mt-0.5 text-[11.5px] text-ink-3 [font-feature-settings:'tnum']">
      {children}
    </div>
  );
}
